import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ImageSeparator from '../components/ImageSeparator';
import {
  Clock,
  Stethoscope,
  Pill,
  Activity,
  Building2,
  CheckCircle,
  ArrowRight,
  MapPin,
  Users,
  Heart
} from 'lucide-react';

const UrgentCare = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const facilities = [
    {
      icon: Stethoscope,
      title: "Urgent Care & Clinical Treatment",
      description: "100% coverage for non-emergency medical visits and evaluations at AMHAS Urgent Care Facilities",
      image: "/urgentcare_doctor_Care_1.png"
    },
    {
      icon: Activity,
      title: "Laboratory Services",
      description: "Complete lab work and blood testing with 100% coverage at our facilities",
      image: "/urgentcare_lab_work_1.png"
    },
    {
      icon: Building2,
      title: "Radiology & Diagnostic Imaging",
      description: "Advanced imaging services including X-rays and diagnostic scans",
      image: "/urgentcare_lab_3.png"
    },
    {
      icon: Pill,
      title: "Pharmacy Services",
      description: "On-site pharmacy with essential pharmaceuticals and medications",
      image: "/urgentcare_payments_community.png"
    },
    {
      icon: Clock,
      title: "Observation Beds",
      description: "Extended non-surgical treatment and monitoring capabilities",
      image: "/urgentcare_beds.png"
    },
    {
      icon: Heart,
      title: "Non-Surgical Emergency Care",
      description: "Immediate care for urgent but non-surgical medical situations",
      image: "/urgentcare_nurse_triage_1.png"
    }
  ];

  const features = [
    "24 hours a day, 365 days a year operation",
    "World-class engineering standards reviewed by U.S.-licensed contractors",
    "Full vertical integration of medical services",
    "Onsite pharmacies for immediate medication access",
    "AI-assisted telehealth integration",
    "Western clinical oversight and quality standards",
    "Biometric ID verification for instant coverage validation",
    "Low-cost services available to surrounding community"
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/urgentcare_front_buildign.png" 
            alt="AMHAS Urgent Care Center" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              AMHAS Urgent Healthcare Centers
            </h1>
            <p className="text-xl md:text-2xl text-primary-100 mb-8">
              Localized community-based world-class healthcare 24/7
            </p>
            <p className="text-lg text-primary-200 max-w-3xl mx-auto">
              World-class urgent care centers engineered from the ground up to deliver Western-standard healthcare. 
              Each facility is designed with world-class engineering standards, allowing us to deploy clinics rapidly 
              while maintaining exceptional quality and low operating costs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-700 mb-4">
                AMHAS is committed to providing immediate, reliable, and affordable healthcare access 
                for school-aged children (ages 2-18). At launch, we will establish geographically 
                centralized urgent care centers to ensure every student can receive high-quality, 
                in-person care when needed.
              </p>
              <p className="text-lg text-gray-700 mb-4">
                At the same time, we will rapidly scale our telehealth services and AI-powered clinical 
                tools directly within schools, enabling fast, on-site assessments and remote medical support.
              </p>
              <p className="text-lg text-gray-700">
                Our insurance program guarantees that students receive comprehensive non-emergency care 
                at no additional cost beyond the annual enrollment fee. These urgent care facilities 
                will also offer low-cost services to the surrounding community, reinforcing our mission 
                to deliver accessible healthcare for all while prioritizing the students we serve.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="/urgentcare_full_site_plan.png" 
                alt="AMHAS Urgent Care Site Plan" 
                className="w-full rounded-lg shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Separator Banner */}
      <ImageSeparator 
        image="/urgentcare_lab_3.png"
        title="Our Centers"
        subtitle="World-class urgent care facilities engineered to deliver Western-standard healthcare"
      />

      {/* Facilities Overview */}
      <section className="relative section-padding bg-gray-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/urgentcare_pharm_front.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Our Centers</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Our world-class urgent care centers are engineered from the ground up to deliver 
              Western-standard healthcare in a repeatable, scalable, and vertically integrated model.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {facilities.map((facility, index) => {
              const Icon = facility.icon;
              return (
                <motion.div
                  key={facility.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <img 
                    src={facility.image} 
                    alt={facility.title} 
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                      <Icon size={24} className="text-primary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{facility.title}</h3>
                    <p className="text-gray-600">{facility.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Site Plan Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="section-title">Standardized Site Plan</h2>
            <p className="section-subtitle max-w-4xl mx-auto">
              Our standardized site plan will be used for every AMHAS urgent care facility across 
              all countries in which we operate. Each center is engineered to world-class specifications 
              and reviewed by U.S.–licensed contractors to ensure the highest standards of safety, 
              quality, and performance.
            </p>
            <p className="text-lg text-gray-700 mt-4 max-w-3xl mx-auto">
              Our mission is to bring the full experience of U.S.-level healthcare to The Gambia—from 
              engineering and clinical excellence to customer service, patient comfort, and the quality 
              of all medical equipment and supplies. Every AMHAS facility is designed so that children 
              receive care identical in quality to what they would expect in the United States.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src="/urgentcare_front_area_1.png" 
                alt="Front Area" 
                className="w-full rounded-lg shadow-lg"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src="/urgentcare_front_waiting_Area.png" 
                alt="Waiting Area" 
                className="w-full rounded-lg shadow-lg"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src="/urgentcare_front_desk.png" 
                alt="Front Desk" 
                className="w-full rounded-lg shadow-lg"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src="/urgentcare_doctor_greet_1.png" 
                alt="Doctor Greeting" 
                className="w-full rounded-lg shadow-lg"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-6">Key Features</h2>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Every AMHAS Urgent Healthcare Center includes:
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={feature}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-start space-x-3"
              >
                <CheckCircle size={24} className="text-yellow-300 mt-1 flex-shrink-0" />
                <span className="text-lg">{feature}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Care Section */}
      <section className="section-padding bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="section-title">Serving Students & Community</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              AMHAS Urgent Healthcare Centers provide care to both enrolled students and the surrounding community
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="card p-8 bg-white"
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                  <Users size={32} className="text-primary-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Enrolled Students</h3>
              </div>
              <p className="text-lg text-gray-700 mb-4">
                All enrolled students (ages 2-18) receive <strong>100% coverage</strong> at AMHAS Urgent Healthcare Centers with no additional cost beyond the annual enrollment fee.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-primary-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">100% coverage for all services</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-primary-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">No additional fees at point of care</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-primary-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Biometric ID verification for instant access</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-primary-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">24/7 access to all services</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="card p-8 bg-white"
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-accent-100 rounded-full flex items-center justify-center mr-4">
                  <Heart size={32} className="text-accent-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Community Members</h3>
              </div>
              <p className="text-lg text-gray-700 mb-4">
                Members of the surrounding community can access the same world-class healthcare services at <strong>low-cost premium rates</strong>, making quality care accessible to all.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Affordable, transparent pricing</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Same world-class facilities and services</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">24/7 access to urgent care services</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Supporting sustainable healthcare delivery</span>
                </li>
              </ul>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="bg-white rounded-lg p-6 max-w-3xl mx-auto">
              <p className="text-lg text-gray-700">
                By serving both enrolled students and the community, AMHAS Urgent Healthcare Centers create an additional revenue stream that strengthens long-term sustainability while ensuring that every child receives the care they need at no additional cost.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Coverage Information */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="section-title">100% Coverage at AMHAS Facilities</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              All services at AMHAS Urgent Healthcare Centers are covered at 100% for enrolled students, 
              with no additional cost beyond the annual enrollment fee.
            </p>
          </motion.div>

          <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Covered Services</h3>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-2">
                    <CheckCircle size={20} className="text-primary-600" />
                    <span>Urgent Care & Clinical Treatment Services</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle size={20} className="text-primary-600" />
                    <span>Laboratory Services and Blood Work</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle size={20} className="text-primary-600" />
                    <span>Radiology and Diagnostic Imaging</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle size={20} className="text-primary-600" />
                    <span>Essential Pharmaceuticals and Medications</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle size={20} className="text-primary-600" />
                    <span>Observation Beds for Extended Treatment</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle size={20} className="text-primary-600" />
                    <span>Non-Surgical Emergency Care</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <CheckCircle size={20} className="text-primary-600" />
                    <span>AI-Powered Telehealth Consultations</span>
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6">How It Works</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Child Intake</h4>
                      <p className="text-gray-600">Student arrives at AMHAS Urgent Care Facility</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Coverage Validation</h4>
                      <p className="text-gray-600">AMHAS ID card scanned with biometric matching to validate coverage</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Initial Triage</h4>
                      <p className="text-gray-600">Vitals collected via remote nurse and AI vitals device or by nurse at facility</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Care Given</h4>
                      <p className="text-gray-600">World-class doctor at urgent care facility or via AI & Telehealth provides care</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              World-Class Healthcare Where It's Needed Most
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              As we scale, AMHAS Urgent Healthcare Centers are being established while our AI and 
              telehealth services expand to all schools, ensuring comprehensive care delivery through 
              both pathways.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link to="/student-enrollment" className="btn-secondary text-lg px-8 py-4">
                Enroll Your Child
              </Link>
              <Link to="/coverage-pricing" className="btn-secondary text-lg px-8 py-4">
                View Coverage Details
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default UrgentCare;

